import type {AtelierActorAsset,AtelierActorFrame,CameraState} from '../../shared';

export interface PaintedBounds {left:number;right:number;bottom:number;top:number}
interface FramingDraw {pos:ArrayLike<number>;joints:ArrayLike<number>;weights:ArrayLike<number>;indices:Uint32Array}
interface FramingAsset {draws:FramingDraw[]}
export const FOLIO_SLOPE=.085;
const C=1/Math.hypot(1,FOLIO_SLOPE),S=FOLIO_SLOPE*C;
/** Camera-space bounds of drawn triangles, not a sphere containing unused
 * vertices, tails and weapon depth. Called at recipe load/resize, never per RAF. */
export function paintedBounds(asset:FramingAsset,frame:AtelierActorFrame):PaintedBounds {
 const b={left:Infinity,right:-Infinity,bottom:Infinity,top:-Infinity},p=frame.palette,m=frame.model;
 for(const draw of asset.draws){const seen=new Uint8Array(draw.pos.length/3);
  for(const v of draw.indices){if(seen[v])continue;seen[v]=1;
   const x=draw.pos[v*3],y=draw.pos[v*3+1],z=draw.pos[v*3+2];let a=0,c=0,e=0;
   for(let k=0;k<4;k++){const w=draw.weights[v*4+k];if(!w)continue;const o=draw.joints[v*4+k]*16;
    a+=(p[o]*x+p[o+4]*y+p[o+8]*z+p[o+12])*w;c+=(p[o+1]*x+p[o+5]*y+p[o+9]*z+p[o+13])*w;e+=(p[o+2]*x+p[o+6]*y+p[o+10]*z+p[o+14])*w;
   }
   const wx=m[0]*a+m[4]*c+m[8]*e+m[12],wy=m[1]*a+m[5]*c+m[9]*e+m[13],wz=m[2]*a+m[6]*c+m[10]*e+m[14],screenY=wy*C-wz*S;
   b.left=Math.min(b.left,wx);b.right=Math.max(b.right,wx);b.bottom=Math.min(b.bottom,screenY);b.top=Math.max(b.top,screenY);
  }
 }
 return b;
}
/** Eight corners per influencing bone conservatively contain its drawn points.
 * Weighted skin points lie inside their convex union. This terminal-only proxy
 * keeps ears/props in frame without scanning dressed GLBs on every frame. */
export function paintedProxy(asset:AtelierActorAsset):FramingAsset {
 const boxes=new Map<number,{lo:number[];hi:number[]}>();
 for(const draw of asset.draws){const seen=new Uint8Array(draw.pos.length/3);
  for(const v of draw.indices){if(seen[v])continue;seen[v]=1;
   for(let k=0;k<4;k++){if(draw.weights[v*4+k]<=0)continue;const bone=draw.joints[v*4+k];
    let b=boxes.get(bone);if(!b){b={lo:[Infinity,Infinity,Infinity],hi:[-Infinity,-Infinity,-Infinity]};boxes.set(bone,b);}
    for(let a=0;a<3;a++){b.lo[a]=Math.min(b.lo[a],draw.pos[v*3+a]);b.hi[a]=Math.max(b.hi[a],draw.pos[v*3+a]);}
   }
  }
 }
 const pos=new Float32Array(boxes.size*24),joints=new Uint8Array(boxes.size*32),weights=new Float32Array(boxes.size*32),indices=new Uint32Array(boxes.size*8);
 let v=0;for(const [bone,b] of boxes)for(let corner=0;corner<8;corner++,v++){
  for(let a=0;a<3;a++)pos[v*3+a]=(corner&(1<<a)?b.hi:b.lo)[a];
  joints[v*4]=bone;weights[v*4]=1;indices[v]=v;
 }
 return {draws:[{pos,joints,weights,indices}]};
}
/** The fighters occupy the page; blank masonry is not part of their fit. A
 * steady orthographic frame leaves 9% above and 12% below the actual drawing. */
export function folioCamera(bounds:PaintedBounds[],aspect:number):CameraState {
 const left=Math.min(...bounds.map(b=>b.left)),right=Math.max(...bounds.map(b=>b.right));
 const bottom=Math.min(...bounds.map(b=>b.bottom)),top=Math.max(...bounds.map(b=>b.top));
 const half=Math.max((top-bottom)/1.58,(right-left)/1.9/Math.max(.2,aspect));
 const x=(left+right)/2,y=((top+bottom)/2-.03*half)/C;
 return {eye:[x,y+7*FOLIO_SLOPE,7],target:[x,y,0],fovYDeg:25,dutchDeg:0,orthoHalfH:half,near:.1,far:30};
}
